/**
 * The real transport. ONE `fetch` FOR THE WHOLE SURFACE.
 *
 * Every method below is a path and a shape; the credential, the error decoding and the abort
 * plumbing happen once, in `request`. That is what makes swapping fixtures for the network a
 * one-line change in ./cabinet-api-source.ts rather than a change in every screen.
 *
 * BEARER ONLY, NO COOKIES, CROSS-ORIGIN. adr/0002 puts the cabinet on `authors.moonx.app` and the
 * API on `moonx.art/authors-api` — different origins — so `credentials` is left at its default
 * ('same-origin'), which sends nothing here, and the token travels in the header. Setting
 * `credentials: 'include'` would ask for a cookie the platform does not issue and would force the
 * API into `Access-Control-Allow-Credentials`, which is the opposite of the ruling.
 *
 * IT RUNS ONLY IN A BUILD THAT SETS `VITE_AUTHORS_API_LIVE=true` - `dev:stack` in this app's
 * package.json does, against the local stack. It was written before any build did, next to the
 * mock, because a transport written later against screens that grew up on fixtures is a transport
 * that discovers six shape mismatches on its first day.
 */
import { AUTHORS_API_BASE_URL } from '../../../config';
import { sendWithSession } from '../../auth/session/send-with-session';
import { ApiProblem, NotFoundFailure, TransportFailure, retryAfterOf } from './problem';
import type { CabinetApi } from './cabinet-api';
import type { OperationBody, OperationQuery, Problem, PublicId } from './generated';

/**
 * How the app gets a Firebase ID token.
 *
 * NOT IMPLEMENTED HERE, AND THAT IS A BOUNDARY RATHER THAN AN OMISSION: sign-in and the `/enter`
 * hand-off are their own surface, and this app must not grow a second Firebase client. Whoever
 * owns the session hands one function in. Returning null is a real state — the cold-start visitor
 * who has not signed in — and produces a 401 the router turns into a redirect.
 * `forceRefresh` is asked for at most once per request, by the shared 401 rule `request` runs under.
 */
export type AccessTokenSource = (forceRefresh?: boolean) => Promise<string | null>;

export interface HttpCabinetApiOptions {
  readonly baseUrl?: string;
  readonly getAccessToken: AccessTokenSource;
}

type QueryValue = string | number | boolean | readonly string[] | undefined | null;

function toSearch(query: Readonly<Record<string, QueryValue>> | undefined): string {
  if (query === undefined) {
    return '';
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      // `status` on listOwnArticles is `readonly ArticleStatus[]`. OpenAPI's default for a query
      // array is `style: form, explode: true`, which is one repeated key per member.
      for (const member of value) {
        params.append(key, String(member));
      }
      continue;
    }
    params.append(key, String(value));
  }
  const search = params.toString();
  return search.length > 0 ? `?${search}` : '';
}

function isProblem(value: unknown): value is Problem {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<Problem>;
  return typeof candidate.error_code === 'string' && typeof candidate.status === 'number';
}

export function createHttpCabinetApi(options: HttpCabinetApiOptions): CabinetApi {
  const baseUrl = options.baseUrl ?? AUTHORS_API_BASE_URL;

  async function request(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    init: {
      query?: Readonly<Record<string, QueryValue>> | undefined;
      body?: unknown;
      accept?: string;
      signal?: AbortSignal | undefined;
    } = {},
  ): Promise<unknown> {
    // ONE ATTEMPT PER TOKEN, AND THE 401 RULE IS NOT DECIDED HERE: a bare 401 earns one forced
    // refresh and one retry, and a refused credential ends the session, for all five of this app's
    // screen clients in one place (features/auth/session/send-with-session.ts).
    const response = await sendWithSession(options.getAccessToken, async (token) => {
      const headers = new Headers({ Accept: init.accept ?? 'application/json' });
      if (token !== null) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      if (init.body !== undefined) {
        headers.set('Content-Type', 'application/json');
      }
      try {
        return await fetch(`${baseUrl}${path}${toSearch(init.query)}`, {
          method,
          headers,
          body: init.body === undefined ? null : JSON.stringify(init.body),
          ...(init.signal === undefined ? {} : { signal: init.signal }),
        });
      } catch (cause) {
        // A rejected fetch never reached the API: offline, DNS, TLS, or a CORS preflight refusal.
        // An AbortError is the caller's own doing and must stay an AbortError so use-async can
        // ignore it rather than painting an error state over a screen the reader has left.
        if (cause instanceof DOMException && cause.name === 'AbortError') {
          throw cause;
        }
        throw new TransportFailure(
          'The request did not reach the platform.',
          cause === undefined ? undefined : { cause },
        );
      }
    });

    if (response.status === 204) {
      return undefined;
    }

    if (!response.ok) {
      const retryAfter = retryAfterOf(response.headers);
      let payload: unknown;
      try {
        payload = await response.json();
      } catch {
        payload = undefined;
      }
      if (isProblem(payload)) {
        throw new ApiProblem(payload, retryAfter);
      }
      if (response.status === 404) {
        // EXPECTED, not a deployment fault. `Problem.error_code` is required and its closed enum
        // has no not-found member, so a 404 on this API cannot carry one — see problem.ts.
        throw new NotFoundFailure('That is no longer there.', 'gone');
      }
      // A non-problem error body is a deployment fault (a proxy's own 502 page, say). Say the
      // status rather than inventing an error_code that no screen could branch on correctly.
      throw new TransportFailure(`The platform answered ${String(response.status)}.`);
    }

    if (init.accept === 'text/csv') {
      return await response.text();
    }
    return await response.json();
  }

  return {
    getAuthorSession: (signal) =>
      request('GET', '/v1/me', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['getAuthorSession']>>
      >,

    getDashboard: (query, signal) =>
      request('GET', '/v1/dashboard', { query, signal }) as Promise<
        Awaited<ReturnType<CabinetApi['getDashboard']>>
      >,

    listArticleStats: (query, signal) =>
      request('GET', '/v1/dashboard/articles', { query, signal }) as Promise<
        Awaited<ReturnType<CabinetApi['listArticleStats']>>
      >,

    getArticleSummary: (articleId: PublicId, signal) =>
      request('GET', `/v1/articles/${encodeURIComponent(articleId)}/summary`, {
        signal,
      }) as Promise<Awaited<ReturnType<CabinetApi['getArticleSummary']>>>,

    listOwnArticles: (query: OperationQuery<'listOwnArticles'> | undefined, signal) =>
      request('GET', '/v1/articles', {
        query: query as Readonly<Record<string, QueryValue>> | undefined,
        signal,
      }) as Promise<Awaited<ReturnType<CabinetApi['listOwnArticles']>>>,

    createDraft: () =>
      request('POST', '/v1/articles') as Promise<Awaited<ReturnType<CabinetApi['createDraft']>>>,

    archiveOwnArticle: (articleId: PublicId) =>
      request('POST', `/v1/articles/${encodeURIComponent(articleId)}/archive`) as Promise<
        Awaited<ReturnType<CabinetApi['archiveOwnArticle']>>
      >,

    unarchiveOwnArticle: (articleId: PublicId) =>
      request('POST', `/v1/articles/${encodeURIComponent(articleId)}/unarchive`) as Promise<
        Awaited<ReturnType<CabinetApi['unarchiveOwnArticle']>>
      >,

    deleteNeverPublishedDraft: async (articleId: PublicId) => {
      await request('DELETE', `/v1/articles/${encodeURIComponent(articleId)}`);
    },

    withdrawArticle: (articleId: PublicId) =>
      request('POST', `/v1/articles/${encodeURIComponent(articleId)}/withdraw`) as Promise<
        Awaited<ReturnType<CabinetApi['withdrawArticle']>>
      >,

    pullScheduledArticle: (articleId: PublicId) =>
      request('POST', `/v1/articles/${encodeURIComponent(articleId)}/pull`) as Promise<
        Awaited<ReturnType<CabinetApi['pullScheduledArticle']>>
      >,

    listCommentsOnMyArticles: (query, signal) =>
      request('GET', '/v1/comments', { query, signal }) as Promise<
        Awaited<ReturnType<CabinetApi['listCommentsOnMyArticles']>>
      >,

    replyToComment: (commentId: PublicId, body: OperationBody<'replyToComment'>) =>
      request('POST', `/v1/comments/${encodeURIComponent(commentId)}/replies`, {
        body,
      }) as Promise<Awaited<ReturnType<CabinetApi['replyToComment']>>>,

    getPaymentsView: (signal) =>
      request('GET', '/v1/payments', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['getPaymentsView']>>
      >,

    listStatements: (query, signal) =>
      request('GET', '/v1/payments/statements', { query, signal }) as Promise<
        Awaited<ReturnType<CabinetApi['listStatements']>>
      >,

    getStatement: (period: string, signal) =>
      request('GET', `/v1/payments/statements/${encodeURIComponent(period)}`, {
        signal,
      }) as Promise<Awaited<ReturnType<CabinetApi['getStatement']>>>,

    getStatementCsv: (period: string, signal) =>
      request('GET', `/v1/payments/statements/${encodeURIComponent(period)}`, {
        accept: 'text/csv',
        signal,
      }) as Promise<string>,

    getPayoutMethod: (signal) =>
      request('GET', '/v1/payout-method', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['getPayoutMethod']>>
      >,

    // ---- the account's own screens (CabinetAccountApi in ./cabinet-api.ts) ----------------------

    // A 403 REAUTH_REQUIRED leaves here as an ApiProblem like any other refusal: re-authenticating
    // needs a click (a popup must open inside one), so the screen decides, never this transport.
    // Design 25's `legalName` and `detailsTrue` ride in the same body.
    updatePayoutMethod: (body) =>
      request('PUT', '/v1/payout-method', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['updatePayoutMethod']>>
      >,

    getOwnProfile: (signal) =>
      request('GET', '/v1/profile', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['getOwnProfile']>>
      >,

    setBirthData: (body) =>
      request('PUT', '/v1/profile/birth-data', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['setBirthData']>>
      >,

    // What the author typed goes in the query string of OUR origin, and the platform logs no query
    // string (it cuts it off every problem's `instance` and its log line).
    searchBirthPlaces: (query, signal) =>
      request('GET', '/v1/profile/birth-places', {
        query: query as Readonly<Record<string, QueryValue>>,
        signal,
      }) as Promise<Awaited<ReturnType<CabinetApi['searchBirthPlaces']>>>,

    clearBirthData: async () => {
      await request('DELETE', '/v1/profile/birth-data');
    },

    setComputedSignsVisibility: (body) =>
      request('PUT', '/v1/profile/computed-signs-visibility', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['setComputedSignsVisibility']>>
      >,

    setEarningsVisibility: (body) =>
      request('PUT', '/v1/settings/earnings-visibility', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['setEarningsVisibility']>>
      >,

    setTipsVisibility: (body) =>
      request('PUT', '/v1/settings/tips-visibility', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['setTipsVisibility']>>
      >,

    requestAccountDeletion: (body) =>
      request('POST', '/v1/account/deletion', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['requestAccountDeletion']>>
      >,

    listNotifications: (query, signal) =>
      request('GET', '/v1/notifications', { query, signal }) as Promise<
        Awaited<ReturnType<CabinetApi['listNotifications']>>
      >,

    markNotificationsRead: (body) =>
      request('POST', '/v1/notifications/read', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['markNotificationsRead']>>
      >,

    getNotificationPreferences: (signal) =>
      request('GET', '/v1/notification-preferences', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['getNotificationPreferences']>>
      >,

    updateNotificationPreferences: (body) =>
      request('PUT', '/v1/notification-preferences', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['updateNotificationPreferences']>>
      >,

    // ---- what readers send, and who they were (CabinetReaderApi in ./cabinet-api.ts) -----------

    listSupporterMessages: (query, signal) =>
      request('GET', '/v1/supporter-messages', { query, signal }) as Promise<
        Awaited<ReturnType<CabinetApi['listSupporterMessages']>>
      >,

    // A legacy message id is an UPPERCASE UUID served exactly as stored, and it goes back the same
    // way: `encodeURIComponent` escapes nothing in a UUID, so the case survives the path.
    replyToSupporterMessage: (messageId, body) =>
      request('POST', `${messagePath(messageId)}/reply`, { body }) as Promise<
        Awaited<ReturnType<CabinetApi['replyToSupporterMessage']>>
      >,

    // No body on these three: the contract declares none, and none is sent.
    retractOwnReply: (messageId) =>
      request('POST', `${messagePath(messageId)}/reply/retract`) as Promise<
        Awaited<ReturnType<CabinetApi['retractOwnReply']>>
      >,

    unhideSupporterMessage: (messageId) =>
      request('POST', `${messagePath(messageId)}/unhide`) as Promise<
        Awaited<ReturnType<CabinetApi['unhideSupporterMessage']>>
      >,

    // 202 with a JSON body: `request` reads any 2xx but 204 as JSON.
    requestSupporterMessageTranslation: (messageId, body) =>
      request('POST', `${messagePath(messageId)}/translation`, { body }) as Promise<
        Awaited<ReturnType<CabinetApi['requestSupporterMessageTranslation']>>
      >,

    getAudienceBreakdown: (query, signal) =>
      request('GET', '/v1/dashboard/audience', { query, signal }) as Promise<
        Awaited<ReturnType<CabinetApi['getAudienceBreakdown']>>
      >,

    // ---- the author's earlier months (CabinetEarlierMonthsApi in ./cabinet-api.ts) ------------

    // Written last, so that the payments reads above - cited by line from other files - kept theirs.
    listEarlierMonths: (signal) =>
      request('GET', '/v1/payments/earlier-months', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['listEarlierMonths']>>
      >,

    // ---- the Statistics chart (design 18) - written last for the same reason ----------------

    getDashboardDays: (query, signal) =>
      request('GET', '/v1/dashboard/days', { query, signal }) as Promise<
        Awaited<ReturnType<CabinetApi['getDashboardDays']>>
      >,

    // ---- Payment details' agreements and countries (design 25) - written last for the same reason

    listAgreements: (signal) =>
      request('GET', '/v1/agreements', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['listAgreements']>>
      >,

    acceptAgreements: (body) =>
      request('POST', '/v1/agreements/acceptances', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['acceptAgreements']>>
      >,

    listCountries: (signal) =>
      request('GET', '/v1/rules/countries', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['listCountries']>>
      >,

    // The application's state, for an applicant's "Getting started" on the Overview (design 25).
    getApplication: (signal) =>
      request('GET', '/v1/application', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['getApplication']>>
      >,

    // Video (v1.0.29). A video draft sends no body; a live stream's says `stream` (1.2.0).
    createVideoDraft: (body) =>
      request('POST', '/v1/videos', { body }) as Promise<
        Awaited<ReturnType<CabinetApi['createVideoDraft']>>
      >,

    requestVideoAccess: () =>
      request('POST', '/v1/video-access') as Promise<
        Awaited<ReturnType<CabinetApi['requestVideoAccess']>>
      >,

    requestStreamAccess: () =>
      request('POST', '/v1/stream-access') as Promise<
        Awaited<ReturnType<CabinetApi['requestStreamAccess']>>
      >,

    listVideoStats: (signal) =>
      request('GET', '/v1/dashboard/videos', { signal }) as Promise<
        Awaited<ReturnType<CabinetApi['listVideoStats']>>
      >,
  };
}

/** The one supporter message's address; every act on it is a path below this. */
function messagePath(messageId: PublicId): string {
  return `/v1/supporter-messages/${encodeURIComponent(messageId)}`;
}
